import React, { useContext, useEffect } from "react";
import {
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { useTheme } from "@material-ui/styles";
import {
  InputDate,
  assetIds,
  ThemedAsset,
  InformationMessage,
} from "nemuru-components";
import { Controller, useForm } from "react-hook-form";
import { DocumentType } from "@nemuru-eng/js-api-client";
import { ProcessContext } from "../../../../../contexts/ProcessContext";
import { ConfigContext } from "../../../../../contexts/ConfigContext";
import { genderTypesConfig } from "../../../kyc/shared/config.jsx";
import { useNationalities } from "../../../../../utils/hooks/useNationalities/useNationalities";
import { objectTypesConfigToArray } from "../../../../../utils/objectTypesConfigToArray";
import { validateKeyboardInputDate } from "../../../../../utils/validations";
import IconTitle from "../../../../shared/components/IconTitle";
import MainButton from "../../../../shared/components/MainButton";
import Text from "../../../../shared/components/Text";
import { ICON_TITLE_HEIGHT } from "../../../../../config/config";

export const IdVerificationExtractedDataForm = ({
  onSubmitForm,
  savedValues,
}) => {
  const { palette } = useTheme();
  const {
    state: { idDocumentVerification, participant },
    dispatch,
  } = useContext(ProcessContext);

  const { extractedData, extractedDataLoading, extractedDataError } =
    idDocumentVerification;

  const onboardingDetailsId = participant.onboardingDetails?.id;

  const fetchExtractedData = () => {
    dispatch({
      type: "REQUEST_GET_ID_DOCUMENT_VERIFICATION_EXTRACTED_DATA",
      payload: onboardingDetailsId,
    });
  };

  useEffect(() => {
    if (onboardingDetailsId && !extractedData && !extractedDataLoading) {
      fetchExtractedData();
    }
  }, [onboardingDetailsId]);

  if (extractedDataLoading || (!extractedData && !extractedDataError)) {
    return (
      <>
        <Box className="appear-anim">
          <IconTitle
            title={<Text tId="ID_VERIFICATION_EXTRACTED_DATA_FORM_TITLE" />}
            subtitle={
              <Text tId="ID_VERIFICATION_EXTRACTED_DATA_FORM_SUBTITLE" />
            }
          />
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          flex={1}
          py={4}
        >
          <CircularProgress color="primary" />
          <Box mt={2}>
            <Typography
              align="center"
              variant="body2"
              style={{ color: palette.text.title }}
            >
              <Text tId="ID_VERIFICATION_EXTRACTED_DATA_LOADING" />
            </Typography>
          </Box>
        </Box>
        <Box>
          <MainButton disabled />
        </Box>
      </>
    );
  }

  if (extractedDataError) {
    return (
      <>
        <Box className="appear-anim">
          <IconTitle
            title={<Text tId="ID_VERIFICATION_EXTRACTED_DATA_FORM_TITLE" />}
            subtitle={
              <Text tId="ID_VERIFICATION_EXTRACTED_DATA_FORM_SUBTITLE" />
            }
          />
        </Box>
        <Box
          flex={1}
          display="flex"
          flexDirection="column"
          justifyContent="center"
        >
          <InformationMessage
            variant={{
              type: "error",
              icon: (
                <i className="material-icons-outlined" style={{ fontSize: 22 }}>
                  error_outline
                </i>
              ),
            }}
            isScreenXs={false}
            messageContent={{
              title: (
                <Text tId="ID_VERIFICATION_EXTRACTED_DATA_LOAD_ERROR_TITLE" />
              ),
              body: <Text tId="ID_VERIFICATION_EXTRACTED_DATA_LOAD_ERROR" />,
            }}
          />
        </Box>
        <Box>
          <MainButton onClick={fetchExtractedData}>
            <Text tId="RETRY" />
          </MainButton>
        </Box>
      </>
    );
  }

  return (
    <IdVerificationExtractedDataFormInner
      extractedData={extractedData}
      savedValues={savedValues}
      onSubmitForm={onSubmitForm}
    />
  );
};

const IdVerificationExtractedDataFormInner = ({
  extractedData,
  savedValues,
  onSubmitForm,
}) => {
  const { palette, breakpoints } = useTheme();
  const isMobile = useMediaQuery(breakpoints.down("xs"));

  const {
    state: { dictionaryValues },
  } = useContext(ConfigContext);

  const { nationalities } = useNationalities();
  const genderOptions = objectTypesConfigToArray(genderTypesConfig);

  const documentType = extractedData.document_type;
  const isDateOfExpiryRequired = documentType !== DocumentType.NIE_EU;

  const DOCUMENT_TYPE_SHORT_LABEL = {
    [DocumentType.NIF]: dictionaryValues["DOCUMENT_TYPE_SHORT_NIF"],
    [DocumentType.NIE_EU]: dictionaryValues["DOCUMENT_TYPE_SHORT_NIE_EU"],
    [DocumentType.NIE_NON_EU]:
      dictionaryValues["DOCUMENT_TYPE_SHORT_NIE_NON_EU"],
  };
  const isPlaceOfBirthRequired = documentType === DocumentType.NIF;

  const isValidOption = (options, value) =>
    options.some((o) => o.value === value);

  const { register, getValues, handleSubmit, errors, control } = useForm({
    defaultValues: {
      documentType:
        savedValues?.documentType ?? extractedData.document_type ?? "",
      firstName: savedValues?.firstName ?? extractedData.first_name ?? "",
      lastName: savedValues?.lastName ?? extractedData.last_name ?? "",
      dateOfBirth:
        savedValues?.dateOfBirth ??
        (extractedData.date_of_birth
          ? new Date(extractedData.date_of_birth)
          : null),
      personalIdentificationNumber:
        savedValues?.personalIdentificationNumber ??
        extractedData.personal_identification_number ??
        "",
      nationality:
        savedValues?.nationality ??
        (isValidOption(nationalities, extractedData.nationality)
          ? extractedData.nationality
          : ""),
      gender:
        savedValues?.gender ??
        (isValidOption(genderOptions, extractedData.gender)
          ? extractedData.gender
          : ""),
      issuingCountry:
        savedValues?.issuingCountry ??
        (isValidOption(nationalities, extractedData.issuing_country)
          ? extractedData.issuing_country
          : ""),
      dateOfExpiry:
        savedValues?.dateOfExpiry ??
        (extractedData.date_of_expiry
          ? new Date(extractedData.date_of_expiry)
          : null),
      placeOfBirth:
        savedValues?.placeOfBirth ?? extractedData.place_of_birth ?? "",
    },
  });

  return (
    <>
      <Box className="appear-anim">
        <IconTitle
          icon={
            <ThemedAsset
              id={assetIds.ICON_ID_FRONT}
              width={ICON_TITLE_HEIGHT}
              height={ICON_TITLE_HEIGHT}
              style={{ color: palette.secondary.main }}
            />
          }
          title={<Text tId="ID_VERIFICATION_EXTRACTED_DATA_FORM_TITLE" />}
          subtitle={<Text tId="ID_VERIFICATION_EXTRACTED_DATA_FORM_SUBTITLE" />}
        />
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label={<Text tId="DOCUMENT_TYPE" />}
              value={
                DOCUMENT_TYPE_SHORT_LABEL[documentType] ??
                extractedData.document_type ??
                ""
              }
              disabled
            />
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel id="nationality">
                <Text tId="NATIONALITY" />
              </InputLabel>
              <Controller
                name="nationality"
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: <Text tId="REQUIRED_FIELD" />,
                  },
                }}
                render={(props) => (
                  <>
                    <Select
                      MenuProps={{
                        MenuListProps: { "aria-label": "Select nationality" },
                      }}
                      onChange={(e) => props.onChange(e.target.value)}
                      value={props.value}
                      error={!!errors.nationality}
                    >
                      {nationalities.map((item, index) => (
                        <MenuItem key={index} value={item.value}>
                          {item.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText style={{ color: palette.error.main }}>
                      {errors.nationality?.message}
                    </FormHelperText>
                  </>
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label={<Text tId="FIRST_NAME" />}
              type="text"
              id="firstName"
              name="firstName"
              error={!!errors.firstName}
              helperText={errors.firstName?.message}
              inputProps={{
                ref: register({
                  required: {
                    value: true,
                    message: <Text tId="REQUIRED_FIELD" />,
                  },
                  validate: {
                    notBlank: (v) =>
                      v?.trim() !== "" || <Text tId="REQUIRED_FIELD" />,
                  },
                }),
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label={<Text tId="LAST_NAME" />}
              type="text"
              id="lastName"
              name="lastName"
              error={!!errors.lastName}
              helperText={errors.lastName?.message}
              inputProps={{
                ref: register({
                  required: {
                    value: true,
                    message: <Text tId="REQUIRED_FIELD" />,
                  },
                  validate: {
                    notBlank: (v) =>
                      v?.trim() !== "" || <Text tId="REQUIRED_FIELD" />,
                    twoWords: (v) =>
                      documentType !== DocumentType.NIF ||
                      v?.trim().split(/\s+/).length >= 2 || (
                        <Text tId="LAST_NAME_TWO_WORDS_REQUIRED" />
                      ),
                  },
                }),
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <InputDate
              allowKeyboardPicking={!isMobile}
              locale={dictionaryValues["DATE_LOCALE"]}
              emptyLabel={dictionaryValues["DATE_FORMAT_DAY_MONTH_YEAR"]}
              control={control}
              fullWidth
              variant="inline"
              openTo="year"
              autoOk
              views={["year", "month", "date"]}
              format="dd/MM/yyyy"
              label={<Text tId="BIRTHDATE" />}
              id="dateOfBirth"
              name="dateOfBirth"
              error={!!errors.dateOfBirth}
              helperText={errors.dateOfBirth?.message}
              disableFuture
              rules={{
                required: {
                  value: true,
                  message: <Text tId="REQUIRED_FIELD" />,
                },
                validate: {
                  validate: (value) =>
                    validateKeyboardInputDate({ value, disableFuture: true }),
                  adult: (value) => {
                    if (!value) return undefined;
                    const maxDate = new Date();
                    maxDate.setFullYear(maxDate.getFullYear() - 18);
                    return (
                      value <= maxDate || <Text tId="UNDERAGE_NOT_ALLOWED" />
                    );
                  },
                },
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label={<Text tId="DOCUMENT_NUMBER" />}
              type="text"
              id="personalIdentificationNumber"
              name="personalIdentificationNumber"
              error={!!errors.personalIdentificationNumber}
              helperText={errors.personalIdentificationNumber?.message}
              inputProps={{
                ref: register({
                  required: {
                    value: true,
                    message: <Text tId="REQUIRED_FIELD" />,
                  },
                  validate: {
                    notBlank: (v) =>
                      v?.trim() !== "" || <Text tId="REQUIRED_FIELD" />,
                  },
                }),
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel id="gender">
                <Text tId="GENDER" />
              </InputLabel>
              <Controller
                name="gender"
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: <Text tId="REQUIRED_FIELD" />,
                  },
                }}
                render={(props) => (
                  <>
                    <Select
                      MenuProps={{
                        MenuListProps: { "aria-label": "Select gender" },
                      }}
                      onChange={(e) => props.onChange(e.target.value)}
                      value={props.value}
                      error={!!errors.gender}
                    >
                      {objectTypesConfigToArray(genderTypesConfig).map(
                        (type) => (
                          <MenuItem key={type.value} value={type.value}>
                            {type.display}
                          </MenuItem>
                        )
                      )}
                    </Select>
                    <FormHelperText style={{ color: palette.error.main }}>
                      {errors.gender?.message}
                    </FormHelperText>
                  </>
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel id="issuingCountry">
                <Text tId="ISSUING_COUNTRY" />
              </InputLabel>
              <Controller
                name="issuingCountry"
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: <Text tId="REQUIRED_FIELD" />,
                  },
                }}
                render={(props) => (
                  <>
                    <Select
                      MenuProps={{
                        MenuListProps: {
                          "aria-label": "Select issuing country",
                        },
                      }}
                      onChange={(e) => props.onChange(e.target.value)}
                      value={props.value}
                      error={!!errors.issuingCountry}
                    >
                      {nationalities.map((item, index) => (
                        <MenuItem key={index} value={item.value}>
                          {item.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText style={{ color: palette.error.main }}>
                      {errors.issuingCountry?.message}
                    </FormHelperText>
                  </>
                )}
              />
            </FormControl>
          </Grid>
          {isDateOfExpiryRequired && (
            <Grid item xs={6}>
              <InputDate
                allowKeyboardPicking={!isMobile}
                locale={dictionaryValues["DATE_LOCALE"]}
                emptyLabel={dictionaryValues["DATE_FORMAT_DAY_MONTH_YEAR"]}
                control={control}
                fullWidth
                variant="inline"
                openTo="year"
                autoOk
                views={["year", "month", "date"]}
                format="dd/MM/yyyy"
                label={<Text tId="EXPIRATION" />}
                id="dateOfExpiry"
                name="dateOfExpiry"
                error={!!errors.dateOfExpiry}
                helperText={errors.dateOfExpiry?.message}
                rules={{
                  required: {
                    value: true,
                    message: <Text tId="REQUIRED_FIELD" />,
                  },
                  validate: {
                    validate: (value) =>
                      value
                        ? validateKeyboardInputDate({
                            value,
                            disablePast: true,
                          })
                        : undefined,
                  },
                }}
              />
            </Grid>
          )}
          <Grid item xs={isDateOfExpiryRequired ? 6 : 12}>
            <TextField
              fullWidth
              label={<Text tId="BIRTH_CITY" />}
              placeholder="Bilbao"
              type="text"
              id="placeOfBirth"
              name="placeOfBirth"
              error={!!errors.placeOfBirth}
              helperText={errors.placeOfBirth?.message}
              inputProps={{
                ref: register({
                  required: isPlaceOfBirthRequired
                    ? { value: true, message: <Text tId="REQUIRED_FIELD" /> }
                    : undefined,
                  validate: {
                    notBlank: (v) =>
                      !v || v.trim() !== "" || <Text tId="REQUIRED_FIELD" />,
                  },
                }),
              }}
            />
          </Grid>
        </Grid>
      </Box>
      <Box>
        <MainButton onClick={handleSubmit(() => onSubmitForm(getValues()))} />
      </Box>
    </>
  );
};
